/**
 * A dash page you have opened before shows its data at once.
 *
 * Every dash hook fetched from scratch on every mount, so going back to a page
 * was as slow as the first visit, and changing a filter back to one you had
 * just looked at fetched it again (owner, 2026-10-26: "dash needs to feel
 * snappier when we change things").
 *
 * So, on /dash only, the answers to the dash's GET reads are kept in this
 * tab's memory and served stale-while-revalidate:
 *
 * - younger than FRESH_MS: answered from memory, no request at all;
 * - older, up to STALE_MS: answered from memory at once, and the same request
 *   goes to the network behind it. When the new answer differs, the hooks
 *   that read that endpoint are told (`onDashCacheUpdate`) and load again,
 *   which now lands on the fresh copy;
 * - older than STALE_MS, or never seen: the network, as before.
 *
 * Any write (POST, PUT, PATCH, DELETE) to our API forgets everything, so a page
 * never shows data from before a change it just made. Only answers that
 * succeeded are kept, bounded by size, oldest out, and a reload starts empty.
 * The page's own fetch code is untouched; this sits under it, the way
 * utils/back-replay-fetch.ts does for listing pages.
 */

/** An answer this young is served with no request behind it. */
export const FRESH_MS = 10_000;

/** An answer older than this is not shown; the page waits for the network. */
export const STALE_MS = 5 * 60_000;

/** Total characters kept, about 12 MB of text. Admin tables are large. */
export const MAX_KEPT_CHARS = 12_000_000;

interface KeptAnswer {
  body: string;
  contentType: string;
  at: number;
}

const kept = new Map<string, KeptAnswer>();
let keptChars = 0;
/** Bumped by every clear, so a read that started before a write is not kept after it. */
let generation = 0;
const refreshing = new Set<string>();

type UpdateListener = (key: string) => void;
const updateListeners = new Set<UpdateListener>();
type BusyListener = (busy: boolean) => void;
const busyListeners = new Set<BusyListener>();

function keep(key: string, answer: KeptAnswer): void {
  if (answer.body.length > MAX_KEPT_CHARS) return;
  const previous = kept.get(key);
  if (previous) {
    keptChars -= previous.body.length;
    kept.delete(key);
  }
  kept.set(key, answer);
  keptChars += answer.body.length;
  // Oldest first (a Map keeps insertion order). The newest fits on its own,
  // so the loop stops before reaching it.
  for (const [oldKey, old] of kept) {
    if (keptChars <= MAX_KEPT_CHARS) break;
    keptChars -= old.body.length;
    kept.delete(oldKey);
  }
}

function setRefreshing(key: string, on: boolean): void {
  const before = refreshing.size > 0;
  if (on) refreshing.add(key);
  else refreshing.delete(key);
  const after = refreshing.size > 0;
  if (before !== after) busyListeners.forEach((fn) => fn(after));
}

/** Forget every kept answer. Test seam, and what any write does. */
export function clearDashCache(): void {
  kept.clear();
  keptChars = 0;
  generation += 1;
}

/**
 * Hear when a background refresh brought a different answer for a read whose
 * path starts with `pathPrefix`. Returns the unsubscribe.
 */
export function onDashCacheUpdate(pathPrefix: string, fn: () => void): () => void {
  const listener: UpdateListener = (key) => {
    if (key.startsWith(pathPrefix)) fn();
  };
  updateListeners.add(listener);
  return () => {
    updateListeners.delete(listener);
  };
}

/** Hear when any background refresh starts or all of them end. */
export function onDashCacheBusy(fn: BusyListener): () => void {
  busyListeners.add(fn);
  return () => {
    busyListeners.delete(fn);
  };
}

/** Whether a background refresh is under way now. */
export function isDashCacheBusy(): boolean {
  return refreshing.size > 0;
}

/** Whether `pathname` is a dash page, where the cache is on. */
function onDash(pathname: string): boolean {
  return pathname === '/dash' || pathname.startsWith('/dash/');
}

/**
 * The request's method and key, or null for one this module leaves alone:
 * anything not same-origin under /api/.
 */
export function dashCacheKey(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  origin: string
): { method: string; key: string } | null {
  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
  const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin || !url.pathname.startsWith('/api/')) return null;
  // The session is not dash data, and next-auth must always see the live one.
  if (url.pathname.startsWith('/api/auth/')) return null;
  return { method, key: `${url.pathname}?${url.searchParams.toString()}` };
}

function answerFrom(kept: KeptAnswer): Response {
  return new Response(kept.body, { status: 200, headers: { 'Content-Type': kept.contentType } });
}

/**
 * Wraps `win.fetch`. Returns a function that puts the original back.
 * Installed once from pages/_app.tsx, in the browser only; it does nothing
 * off /dash.
 */
export function installDashFetchCache(win: Window & typeof globalThis, now: () => number = Date.now): () => void {
  const original = win.fetch;
  const origin = win.location.origin;

  const network = async (key: string, input: RequestInfo | URL, init: RequestInit | undefined): Promise<Response> => {
    const startedIn = generation;
    const response = await original.call(win, input, init);
    const contentType = response.headers.get('Content-Type') ?? '';
    if (response.ok && contentType.includes('json')) {
      // Read from a copy, so the caller's body is untouched. A failed read
      // only means this answer is not kept.
      void response.clone().text().then((body) => {
        if (generation === startedIn) keep(key, { body, contentType, at: now() });
      }, () => undefined);
    }
    return response;
  };

  const revalidate = (key: string, input: RequestInfo | URL, init: RequestInit | undefined, shown: string) => {
    if (refreshing.has(key)) return;
    setRefreshing(key, true);
    const startedIn = generation;
    // Without the caller's abort signal: the page may unmount, and the fresh
    // answer is still worth keeping for the next visit.
    const { signal: _signal, ...rest } = init ?? {};
    void original
      .call(win, input instanceof Request ? input.url : input, rest)
      .then(async (response) => {
        if (!response.ok) return;
        const body = await response.text();
        if (generation !== startedIn) return;
        keep(key, { body, contentType: response.headers.get('Content-Type') ?? 'application/json', at: now() });
        if (body !== shown) updateListeners.forEach((fn) => fn(key));
      })
      .catch(() => undefined)
      .finally(() => setRefreshing(key, false));
  };

  const wrapped: typeof fetch = async (input, init) => {
    if (!onDash(win.location.pathname)) return original.call(win, input, init);
    const request = dashCacheKey(input, init, origin);
    if (request === null) return original.call(win, input, init);

    if (request.method !== 'GET') {
      clearDashCache();
      return original.call(win, input, init);
    }

    const answer = kept.get(request.key);
    if (answer) {
      const age = now() - answer.at;
      if (age < FRESH_MS) return answerFrom(answer);
      if (age < STALE_MS) {
        revalidate(request.key, input, init, answer.body);
        return answerFrom(answer);
      }
    }
    return network(request.key, input, init);
  };

  win.fetch = wrapped;
  return () => {
    if (win.fetch === wrapped) win.fetch = original;
  };
}
